import { useState, useRef } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';

const DemoVideo = () => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 tracking-tight">
            使用イメージ動画
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            実際の使用シーンをご覧ください。音声AIがどのようにお客様と会話し、リードを獲得するかをご確認いただけます。
          </p>
        </motion.div>

        <motion.div
          className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <video
            ref={videoRef}
            className="w-full h-auto"
            controls
            muted={isMuted}
            playsInline
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          >
            <source src="/demo-video.mp4" type="video/mp4" />
            お使いのブラウザは動画タグをサポートしていません。
          </video>

          {/* カスタムコントロール（オプション） */}
          <div className="absolute bottom-4 right-4 flex gap-2">
            <button
              onClick={toggleMute}
              className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all backdrop-blur-sm"
              aria-label={isMuted ? "音声をオン" : "音声をオフ"}
            >
              {isMuted ? (
                <VolumeX size={20} />
              ) : (
                <Volume2 size={20} />
              )}
            </button>
          </div>
        </motion.div>

        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p className="text-sm text-gray-500">
            ※音声は初期状態でミュートになっています。音声をオンにするには、動画プレーヤーの音量ボタンをクリックしてください。
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoVideo;
